import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import $ from "jquery";
import Select from "react-select";
import apiurl from "../Api";

const EMPTY_FORM = {
  invoice_no: "",
  invoice_date: "",
  invoice_amount: "",
  ba_name: "",
  ba_code: "",
  gstin: "",
  client_name: "",
  client_code: "",
  location: "",
  service_from_date: "",
  service_to_date: "",
  service_period: "",
  credit_note: "",
  manager_name: "",
  bank_account_no: "",
  ifsc_code: "",
};

const BA_CATEGORY_META = {
  lead_generation_only: {
    category: "Lead Generation Only",
    description: "Convertible Lead Provided",
  },
  account_management: {
    category: "Account Management",
    description: "Client Onboarding + CRM Assistance",
  },
  partial_sourcing: {
    category: "Partial Sourcing",
    description: "Stipend-only OR <50% Candidates Sourced",
  },
  comprehensive_services: {
    category: "Comprehensive Services",
    description: "Lead Gen + Pitching + CRM",
  },
};

const BA_SHARING_RATIO_META = {
  "5_00_ba_share_1_year": "5.00 BA Share (1 Year)",
  "5_00_ba_share_2_years": "5.00 BA Share (2 Years)",
  "20_00_ba_share": "20.00 BA Share",
  "50_00_ba_share": "50.00 BA Share",
};

function humanizeSlug(value) {
  return (value || "")
    .toString()
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildBaCategoryRow(serviceCategory, sharingRatio) {
  const normalizedCategory = (serviceCategory || "").toString().trim().toLowerCase();
  const normalizedRatio = (sharingRatio || "").toString().trim().toLowerCase();
  const categoryMeta = BA_CATEGORY_META[normalizedCategory] || null;
  const categoryName = categoryMeta?.category || humanizeSlug(serviceCategory) || "";
  const categoryDescription = categoryMeta?.description || "-";
  const ratioText = BA_SHARING_RATIO_META[normalizedRatio] || humanizeSlug(sharingRatio) || "-";
  if (!categoryName) return null;
  return {
    id: `ba-${normalizedCategory || "fixed"}`,
    category: categoryName,
    description: categoryDescription,
    display_ratio: ratioText,
  };
}

function toDateInputValue(value) {
  const text = (value || "").toString().trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const dt = new Date(text);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

function parseServicePeriodRange(value) {
  const text = (value || "").toString().trim();
  if (!text) return { from: "", to: "" };
  const matches = text.match(/\d{4}-\d{2}-\d{2}/g);
  if (matches && matches.length >= 2) {
    return { from: matches[0], to: matches[1] };
  }
  return { from: "", to: "" };
}

function parseJsonArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function buildFallbackCategoryRows(categoryValue) {
  const list = parseJsonArray(categoryValue);
  return list
    .map((name, idx) => {
      const category = (name || "").toString().trim();
      if (!category) return null;
      return {
        id: `legacy-${idx}-${category.toLowerCase().replace(/\s+/g, "-")}`,
        category,
        description: "-",
        display_ratio: "-",
      };
    })
    .filter(Boolean);
}

function normalizePostValue(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function normalizeLookupText(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function CreateInvoiceModal({
  show,
  onClose,
  onSuccess,
  mode = "create",
  initialData = null,
}) {
  const isEditMode = mode === "edit";

  const [validationParams, setValidationParams] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const [canViewDecisionSection, setCanViewDecisionSection] = useState(false);
  const [decision, setDecision] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [missingFields, setMissingFields] = useState([]);
  const [missingValidationIds, setMissingValidationIds] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [managerLoading, setManagerLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCategoryLoading, setServiceCategoryLoading] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [validationEntries, setValidationEntries] = useState({});
  const [documentFile, setDocumentFile] = useState(null);
  const [loggedInName, setLoggedInName] = useState("");
  const [sessionPost, setSessionPost] = useState("");
  const [sessionStaffProfile, setSessionStaffProfile] = useState({
    bank_account_no: "",
    ifsc_code: "",
    service_category: "",
    sharing_ratio: "",
  });

  const modalTitle = isEditMode ? "Update Invoice" : "Create Invoice";
  const submitLabel = isEditMode ? "Update Invoice" : "Submit Invoice";
  const isBusinessAssociateUser = sessionPost === "business_associate";
  const isAccountsUser = ["accounts", "accounts_head"].includes(sessionPost);
  const fixedBaCategoryRow = useMemo(
    () =>
      buildBaCategoryRow(
        sessionStaffProfile.service_category,
        sessionStaffProfile.sharing_ratio
      ),
    [sessionStaffProfile.service_category, sessionStaffProfile.sharing_ratio]
  );

  const fetchValidationParams = async () => {
    try {
      setValidationLoading(true);
      const response = await axios.get(`${apiurl}validation_parameters.php?active=1`);
      if (response.data.status === true) {
        setValidationParams(response.data.data || []);
        setCanViewDecisionSection(Number(response.data.view) === 1);
      } else {
        setValidationParams([]);
        setCanViewDecisionSection(false);
      }
    } catch (error) {
      setValidationParams([]);
      setCanViewDecisionSection(false);
    } finally {
      setValidationLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      setVendorLoading(true);
      const response = await axios.get(`${apiurl}save_invoice.php?list=clients`);
      if (response.data.status === true) {
        const rows = response.data.data || [];
        setVendorOptions(
          rows.map((row) => ({
            value: row.id,
            label: row.contact_name
              ? `${row.vendor_name} (${row.contact_name})`
              : row.vendor_name,
            clientName: row.vendor_name || "",
            contactName: row.contact_name || "",
            vendorid: row.vendorid,
            location: row.location || "",
          }))
        );
      } else {
        setVendorOptions([]);
      }
    } catch (error) {
      setVendorOptions([]);
    } finally {
      setVendorLoading(false);
    }
  };

  const fetchBusinessManagers = async () => {
    try {
      setManagerLoading(true);
      const response = await axios.get(`${apiurl}save_invoice.php?list=business_managers`);
      if (response.data.status === true) {
        const rows = response.data.data || [];
        setManagerOptions(
          rows.map((row) => ({
            value: row.id,
            label: (row.name || "").toString().trim(),
            name: (row.name || "").toString().trim(),
          }))
        );
      } else {
        setManagerOptions([]);
      }
    } catch (error) {
      setManagerOptions([]);
    } finally {
      setManagerLoading(false);
    }
  };

  const fetchSessionDisplayName = async () => {
    try {
      const response = await axios.post(`${apiurl}session.php`);
      const displayName = (response.data?.banda_naam || "").toString().trim();
      setLoggedInName(displayName);
      const post = normalizePostValue(response.data?.post || "");
      setSessionPost(post);
      setSessionStaffProfile({
        bank_account_no: (response.data?.bank_account_no || "").toString().trim(),
        ifsc_code: (response.data?.ifsc_code || "").toString().trim(),
        service_category: (response.data?.service_category || "").toString().trim(),
        sharing_ratio: (response.data?.sharing_ratio || "").toString().trim(),
      });
    } catch (error) {
      setLoggedInName("");
      setSessionPost("");
      setSessionStaffProfile({
        bank_account_no: "",
        ifsc_code: "",
        service_category: "",
        sharing_ratio: "",
      });
    }
  };

  const isMissing = (name) => missingFields.includes(name);

  const clearMissing = (name) => {
    setMissingFields((prev) => prev.filter((item) => item !== name));
  };

  const setField = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    clearMissing(name);
  };

  useEffect(() => {
    if (!show) return;
    setServiceCategoryLoading(true);

    fetchValidationParams();
    fetchVendors();
    fetchBusinessManagers();
    fetchSessionDisplayName();
    setMissingFields([]);
    setMissingValidationIds([]);
    setDocumentFile(null);

    if (isEditMode && initialData) {
      const parsedRange = parseServicePeriodRange(initialData.service_period || "");
      const prefill = {
        invoice_no: initialData.invoice_no || "",
        invoice_date: initialData.invoice_date || "",
        invoice_amount: initialData.invoice_amount || "",
        ba_name: initialData.ba_name || "",
        ba_code: initialData.ba_code || "",
        gstin: initialData.gstin || "",
        client_name: initialData.client_name || "",
        client_code: initialData.client_code || "",
        location: initialData.location || "",
        service_from_date: toDateInputValue(initialData.service_from_date || parsedRange.from),
        service_to_date: toDateInputValue(initialData.service_to_date || parsedRange.to),
        service_period: initialData.service_period || "",
        credit_note: initialData.credit_note || "",
        manager_name: initialData.manager_name || "",
        bank_account_no: initialData.ba_bank_account_no || "",
        ifsc_code: initialData.ba_ifsc_code || "",
      };
      setFormValues(prefill);
      setDecision(initialData.decision || "");
      setRejectionReason(initialData.rejection_reason || "");
      setSelectedCategories(parseJsonArray(initialData.category));

      const mapped = {};
      (initialData.validation_items || []).forEach((item) => {
        const pid = Number(item.validation_parameter_id || 0);
        if (!pid) return;
        mapped[pid] = {
          status: item.status || "",
          remark: item.remark || "",
        };
      });
      setValidationEntries(mapped);
    } else {
      setFormValues({ ...EMPTY_FORM, ba_name: loggedInName || "" });
      setDecision("");
      setRejectionReason("");
      setSelectedCategories([]);
      setValidationEntries({});
      setSelectedVendor(null);
      setSelectedManager(null);
    }
  }, [show, isEditMode, initialData]);

  useEffect(() => {
    if (!show || isEditMode) return;
    setFormValues((prev) => ({
      ...prev,
      ba_name: loggedInName || "",
    }));
  }, [show, isEditMode, loggedInName]);

  useEffect(() => {
    if (!show) return;
    if (!isEditMode && sessionPost === "") {
      setServiceCategoryLoading(true);
      return;
    }
    const rows = [];
    if (isBusinessAssociateUser) {
      if (fixedBaCategoryRow) {
        rows.push(fixedBaCategoryRow);
      }
      setFormValues((prev) => ({
        ...prev,
        bank_account_no: sessionStaffProfile.bank_account_no || "",
        ifsc_code: sessionStaffProfile.ifsc_code || "",
      }));
    } else {
      const existingRow = buildBaCategoryRow(
        initialData?.ba_service_category || "",
        initialData?.ba_sharing_ratio || ""
      );
      if (existingRow) {
        rows.push(existingRow);
      } else {
        rows.push(...buildFallbackCategoryRows(initialData?.category));
      }
    }
    setServiceCategories(rows);
    setSelectedCategories(rows.map((item) => item.category).filter(Boolean));
    setServiceCategoryLoading(false);
  }, [
    show,
    isEditMode,
    sessionPost,
    isBusinessAssociateUser,
    fixedBaCategoryRow,
    initialData,
    sessionStaffProfile.bank_account_no,
    sessionStaffProfile.ifsc_code,
  ]);

  useEffect(() => {
    if (!show || !isEditMode || !initialData || vendorOptions.length === 0) return;
    const normalizedClientCode = String(formValues.client_code || "").trim().toLowerCase();
    const normalizedClientName = String(formValues.client_name || "").trim().toLowerCase();
    const looseClientName = normalizeLookupText(formValues.client_name || "");
    const match = vendorOptions.find(
      (option) =>
        String(option.vendorid || "").trim().toLowerCase() === normalizedClientCode ||
        String(option.clientName || "").trim().toLowerCase() === normalizedClientName ||
        (looseClientName &&
          normalizeLookupText(option.clientName || option.label || "") === looseClientName)
    );
    if (match) {
      setSelectedVendor(match);
      const currentLocation = String(formValues.location || "").trim();
      const fallbackLocation = String(match.location || "").trim();
      if (!currentLocation && fallbackLocation) {
        setFormValues((prev) => ({ ...prev, location: fallbackLocation }));
        setMissingFields((prev) => prev.filter((item) => item !== "location"));
      }
    }
  }, [
    show,
    isEditMode,
    initialData,
    vendorOptions,
    formValues.client_code,
    formValues.client_name,
    formValues.location,
  ]);

  useEffect(() => {
    if (!selectedVendor) return;
    const nextClientName = selectedVendor.clientName || selectedVendor.label || "";
    const nextClientCode = selectedVendor.vendorid || "";
    const nextLocation = String(selectedVendor.location || "").trim();
    setFormValues((prev) => ({
      ...prev,
      client_name: nextClientName,
      client_code: nextClientCode,
      location: nextLocation || prev.location || "",
    }));
    setMissingFields((prev) =>
      prev.filter((item) => item !== "client_name" && item !== "client_code" && item !== "location")
    );
  }, [selectedVendor]);

  useEffect(() => {
    if (!show || managerOptions.length === 0) return;
    const current = (formValues.manager_name || "").toString().trim().toLowerCase();
    if (!current) {
      setSelectedManager(null);
      return;
    }
    const match = managerOptions.find(
      (option) => (option.name || "").toString().trim().toLowerCase() === current
    );
    setSelectedManager(match || null);
  }, [show, managerOptions, formValues.manager_name]);

  const updateValidationEntry = (paramId, key, value) => {
    setMissingValidationIds((prev) => prev.filter((id) => id !== Number(paramId)));
    setValidationEntries((prev) => ({
      ...prev,
      [paramId]: {
        status: prev[paramId]?.status || "",
        remark: prev[paramId]?.remark || "",
        [key]: value,
      },
    }));
  };

  const normalizeManagerName = (value) => {
    const text = (value || "").toString().trim();
    if (!text) return "";
    // If any label like "Name (email@x.com)" leaks into state, keep only the name part.
    return text.replace(/\s*\([^)]*@[^)]*\)\s*$/, "").trim();
  };

  const handleSubmit = async () => {
    try {
      if (isEditMode && isAccountsUser) {
        const tdsVal = Number((initialData?.tds_percentage || "").toString().trim());
        const netVal = Number((initialData?.net_payable_amount || "").toString().trim());
        if (!Number.isFinite(tdsVal) || tdsVal <= 0 || !Number.isFinite(netVal) || netVal <= 0) {
          Swal.fire("Warning", "Submit TDS percentage first", "warning");
          return;
        }
      }
      const serviceFrom = (formValues.service_from_date || "").toString().trim();
      const serviceTo = (formValues.service_to_date || "").toString().trim();
      if ((serviceFrom && !serviceTo) || (!serviceFrom && serviceTo)) {
        const periodMissing = !serviceFrom ? "service_from_date" : "service_to_date";
        setMissingFields((prev) => Array.from(new Set([...prev, periodMissing])));
        Swal.fire("Warning", "Please select both Service From Date and Service To Date", "warning");
        return;
      }
      if (serviceFrom && serviceTo && serviceTo < serviceFrom) {
        setMissingFields((prev) =>
          Array.from(new Set([...prev, "service_from_date", "service_to_date"]))
        );
        Swal.fire("Warning", "Service To Date cannot be before Service From Date", "warning");
        return;
      }

      const computedServicePeriod =
        serviceFrom && serviceTo
          ? `${serviceFrom} to ${serviceTo}`
          : (formValues.service_period || "").toString().trim();

      const payloadValues = {
        ...formValues,
        service_from_date: serviceFrom,
        service_to_date: serviceTo,
        service_period: computedServicePeriod,
        manager_name: normalizeManagerName(formValues.manager_name),
        ba_bank_account_no: (formValues.bank_account_no || "").toString().trim(),
        ba_ifsc_code: (formValues.ifsc_code || "").toString().trim().toUpperCase(),
        ba_service_category: isBusinessAssociateUser
          ? (sessionStaffProfile.service_category || "").toString().trim()
          : (isEditMode ? (initialData?.ba_service_category || "").toString().trim() : ""),
        ba_sharing_ratio: isBusinessAssociateUser
          ? (sessionStaffProfile.sharing_ratio || "").toString().trim()
          : (isEditMode ? (initialData?.ba_sharing_ratio || "").toString().trim() : ""),
      };

      if (
        isBusinessAssociateUser &&
        (!payloadValues.ba_bank_account_no ||
          !payloadValues.ba_ifsc_code ||
          !payloadValues.ba_service_category ||
          selectedCategories.length === 0)
      ) {
        Swal.fire(
          "Warning",
          "Bank Account No., IFSC and Service Category are required for Business Associate",
          "warning"
        );
        return;
      }

      const requiredFields = [
        "invoice_no",
        "invoice_date",
        "invoice_amount",
        "client_name",
        "location",
        "manager_name",
      ];

      const missingNames = requiredFields.filter((field) => {
        const value = (payloadValues[field] || "").toString().trim();
        return !value;
      });

      if (canViewDecisionSection) {
        if (!decision) {
          missingNames.push("decision");
        }
        if (decision === "rejected" && !rejectionReason.trim()) {
          missingNames.push("rejection_reason");
        }
      }

      const pendingValidationIds = validationParams
        .filter((param) => (validationEntries[param.id]?.status || "").trim() === "")
        .map((param) => Number(param.id));

      setMissingValidationIds(pendingValidationIds);
      setMissingFields(missingNames);
      if (missingNames.length > 0 || pendingValidationIds.length > 0) {
        Swal.fire({
          icon: "warning",
          title:
            pendingValidationIds.length > 0
              ? "Validation checklist pending"
              : "Fill missing details",
          timer: 1000,
          showConfirmButton: false,
          position: "center",
        });
        return;
      }

      const formData = new FormData();
      if (isEditMode && initialData?.id) {
        formData.append("action", "update");
        formData.append("id", String(initialData.id));
      }

      Object.entries(payloadValues).forEach(([key, value]) => {
        formData.append(key, value ?? "");
      });

      selectedCategories.forEach((cat) => formData.append("category[]", cat));

      validationParams.forEach((param, idx) => {
        const entry = validationEntries[param.id] || { status: "", remark: "" };
        formData.append(`validation[${idx}][id]`, String(param.id));
        formData.append(`validation[${idx}][status]`, entry.status || "");
        formData.append(`validation[${idx}][remark]`, entry.remark || "");
      });

      const payloadDecision = canViewDecisionSection ? (decision || "") : "";
      const payloadRejectionReason = canViewDecisionSection ? (rejectionReason || "") : "";
      formData.append("decision", payloadDecision);
      formData.append("rejection_reason", payloadRejectionReason);
      if (documentFile) {
        formData.append("document", documentFile);
      }

      $("#showLoader").removeClass("d-none").addClass("d-flex");

      const response = await axios.post(`${apiurl}save_invoice.php`, formData);

      if (response.data.status === true) {
        $("#showLoader").removeClass("d-flex").addClass("d-none");
        Swal.fire({
          position: "top-center",
          icon: "success",
          title: response.data.message,
          showConfirmButton: false,
          timer: 2000,
        }).then(() => {
          if (onSuccess) onSuccess();
          onClose();
        });
      } else {
        $("#showLoader").removeClass("d-flex").addClass("d-none");
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      $("#showLoader").removeClass("d-flex").addClass("d-none");
      Swal.fire("Error", "Server Error", "error");
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        .table-head-sky th {
          background: transparent !important;
          color: #000000 !important;
        }
      `}</style>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-semibold">{modalTitle}</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <form id="invoiceForm" className="modal-body px-4" encType="multipart/form-data">
              <div className="mb-4">
                <h6 className="text-info mb-3">CLIENT / VENDOR DETAILS</h6>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Client / Vendor Name <span className="text-danger">*</span></label>
                    <Select
                      className={isMissing("client_name") ? "is-invalid" : ""}
                      classNamePrefix="react-select"
                      isLoading={vendorLoading}
                      options={vendorOptions}
                      value={selectedVendor}
                      onChange={(option) => {
                        setSelectedVendor(option);
                        if (!option) {
                          setField("client_name", "");
                          setField("client_code", "");
                          setField("location", "");
                        }
                      }}
                      placeholder="Select Client"
                    />
                    {!selectedVendor && formValues.client_name ? (
                      <small className="text-muted">Current: {formValues.client_name}</small>
                    ) : null}
                    <input type="hidden" name="client_name" value={formValues.client_name} />
                    <div className="invalid-feedback">Client / Vendor Name is required.</div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Location <span className="text-danger">*</span></label>
                    <input
                      className={`form-control ${isMissing("location") ? "is-invalid" : ""}`}
                      name="location"
                      placeholder="Enter Location"
                      value={formValues.location}
                      onChange={(e) => setField("location", e.target.value)}
                    />
                    <div className="invalid-feedback">Location is required.</div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h6 className="text-info mb-3">INVOICE DETAILS</h6>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Invoice Number <span className="text-danger">*</span></label>
                    <input
                      className={`form-control ${isMissing("invoice_no") ? "is-invalid" : ""}`}
                      name="invoice_no"
                      placeholder="Enter Invoice Number"
                      value={formValues.invoice_no}
                      onChange={(e) => setField("invoice_no", e.target.value)}
                    />
                    <div className="invalid-feedback">Invoice Number is required.</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Invoice Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className={`form-control ${isMissing("invoice_date") ? "is-invalid" : ""}`}
                      name="invoice_date"
                      value={formValues.invoice_date}
                      onChange={(e) => setField("invoice_date", e.target.value)}
                    />
                    <div className="invalid-feedback">Invoice Date is required.</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Invoice Amount (&#8377;): <span className="text-danger">*</span></label>
                    <input
                      className={`form-control ${isMissing("invoice_amount") ? "is-invalid" : ""}`}
                      name="invoice_amount"
                      placeholder="Enter Invoice Amount"
                      value={formValues.invoice_amount}
                      onChange={(e) => setField("invoice_amount", e.target.value)}
                    />
                    <div className="invalid-feedback">Invoice Amount is required.</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">BA Name <span className="text-danger">*</span></label>
                    <input
                      className={`form-control ${isMissing("ba_name") ? "is-invalid" : ""}`}
                      name="ba_name"
                      placeholder="BA Name"
                      value={formValues.ba_name}
                      readOnly
                    />
                    <div className="invalid-feedback">BA Name is required.</div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">GSTIN</label>
                    <input
                      className="form-control"
                      name="gstin"
                      maxLength={15}
                      placeholder="15 character GSTIN"
                      value={formValues.gstin}
                      onChange={(e) => setField("gstin", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Bank Account No.</label>
                    <input
                      className="form-control"
                      name="bank_account_no"
                      placeholder="Bank Account No."
                      value={formValues.bank_account_no}
                      onChange={(e) => setField("bank_account_no", e.target.value)}
                      readOnly={isBusinessAssociateUser}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">IFSC Code</label>
                    <input
                      className="form-control text-uppercase"
                      name="ifsc_code"
                      placeholder="IFSC Code"
                      value={formValues.ifsc_code}
                      onChange={(e) => setField("ifsc_code", e.target.value.toUpperCase())}
                      readOnly={isBusinessAssociateUser}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Service Period</label>
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="date"
                          className={`form-control ${isMissing("service_from_date") ? "is-invalid" : ""}`}
                          name="service_from_date"
                          value={formValues.service_from_date}
                          onChange={(e) => setField("service_from_date", e.target.value)}
                        />
                        <div className="invalid-feedback">From date required.</div>
                        <small className="text-muted d-block mt-1">From</small>
                      </div>
                      <div className="col-6">
                        <input
                          type="date"
                          className={`form-control ${isMissing("service_to_date") ? "is-invalid" : ""}`}
                          name="service_to_date"
                          value={formValues.service_to_date}
                          onChange={(e) => setField("service_to_date", e.target.value)}
                        />
                        <div className="invalid-feedback">To date required.</div>
                        <small className="text-muted d-block mt-1">To</small>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Credit Note</label>
                    <input
                      className="form-control"
                      name="credit_note"
                      placeholder="Enter Credit Note"
                      value={formValues.credit_note}
                      onChange={(e) => setField("credit_note", e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Supporting Documents</label>
                    <input
                      type="file"
                      className="form-control"
                      name="document"
                      onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                    />
                    <small className="text-muted">Max 10MB (ZIP, PDF, Word, PPT, Images)</small>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h6 className="text-info mb-3">SERVICE CATEGORY</h6>

                <table className="table table-bordered">
                  <thead className="table-head-sky">
                    <tr>
                      <th></th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Sharing Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceCategoryLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">Loading categories...</td>
                      </tr>
                    ) : serviceCategories.length > 0 ? (
                      serviceCategories.map((cat) => {
                        const categoryName = cat.category || "";
                        return (
                          <tr key={cat.id}>
                            <td>
                              <input
                                type="checkbox"
                                name="category[]"
                                value={categoryName}
                                checked={selectedCategories.includes(categoryName)}
                                disabled
                                readOnly
                              />
                            </td>
                            <td>{cat.category || "-"}</td>
                            <td>{cat.description || "-"}</td>
                            <td>{cat.display_ratio || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">No active service categories found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mb-4">
                <h6 className="text-info mb-3">SERVICE DELIVERY VALIDATION CHECKLIST</h6>
                {missingValidationIds.length > 0 ? (
                  <div className="text-danger small mb-2">
                    Please select Yes/No/N-A in all checklist rows.
                  </div>
                ) : null}

                <table className="table table-bordered">
                  <thead className="table-head-sky">
                    <tr>
                      <th>Validation Parameter</th>
                      <th>Yes</th>
                      <th>No</th>
                      <th>N/A</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">Loading...</td>
                      </tr>
                    ) : validationParams.length > 0 ? (
                      validationParams.map((param, i) => {
                        const entry = validationEntries[param.id] || { status: "", remark: "" };
                        return (
                          <tr
                            key={param.id}
                            className={missingValidationIds.includes(Number(param.id)) ? "table-danger" : ""}
                          >
                            <td>
                              {param.parameter}
                              <input type="hidden" name={`validation[${i}][id]`} value={param.id} />
                              {missingValidationIds.includes(Number(param.id)) ? (
                                <div className="text-danger small mt-1">Please select one option.</div>
                              ) : null}
                            </td>
                            <td>
                              <input
                                type="radio"
                                name={`validation[${i}][status]`}
                                value="yes"
                                checked={entry.status === "yes"}
                                onChange={() => updateValidationEntry(param.id, "status", "yes")}
                              />
                            </td>
                            <td>
                              <input
                                type="radio"
                                name={`validation[${i}][status]`}
                                value="no"
                                checked={entry.status === "no"}
                                onChange={() => updateValidationEntry(param.id, "status", "no")}
                              />
                            </td>
                            <td>
                              <input
                                type="radio"
                                name={`validation[${i}][status]`}
                                value="na"
                                checked={entry.status === "na"}
                                onChange={() => updateValidationEntry(param.id, "status", "na")}
                              />
                            </td>
                            <td>
                              <input
                                className="form-control form-control-sm"
                                name={`validation[${i}][remark]`}
                                placeholder="Enter Remarks"
                                value={entry.remark || ""}
                                onChange={(e) => updateValidationEntry(param.id, "remark", e.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">No active validation parameter</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {canViewDecisionSection ? (
                <div className="mb-4">
                  <h6 className="text-info mb-3">VALIDATION DECISION</h6>
                  {isMissing("decision") ? <div className="text-danger small mb-2">Please select a decision.</div> : null}

                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      name="decision"
                      value="validated"
                      checked={decision === "validated"}
                      onChange={() => {
                        setDecision("validated");
                        setRejectionReason("");
                        clearMissing("rejection_reason");
                        clearMissing("decision");
                      }}
                    />
                    <label className="form-check-label text-success fw-semibold ml-2">
                      VALIDATED - Proceed to Settlement Processing
                    </label>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      type="radio"
                      name="decision"
                      value="rejected"
                      checked={decision === "rejected"}
                      onChange={() => {
                        setDecision("rejected");
                        clearMissing("decision");
                      }}
                    />
                    <label className="form-check-label text-danger fw-semibold ml-2">
                      REJECTED - Return to BA for Correction (Specify reason below)
                    </label>
                  </div>

                  <label className="form-label">
                    Rejection Reason {decision === "rejected" ? <span className="text-danger">*</span> : null}
                  </label>
                  <textarea
                    className={`form-control ${isMissing("rejection_reason") ? "is-invalid" : ""}`}
                    name="rejection_reason"
                    rows="3"
                    placeholder="Enter Rejection Reason"
                    value={rejectionReason}
                    onChange={(e) => {
                      setRejectionReason(e.target.value);
                      if (e.target.value.trim()) clearMissing("rejection_reason");
                    }}
                    disabled={decision !== "rejected"}
                  ></textarea>
                  <div className="invalid-feedback">Rejection Reason is required.</div>
                </div>
              ) : null}

              <div className="mb-4">
                <h6 className="text-info mb-3">AUTHORIZATION</h6>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Business Manager Name <span className="text-danger">*</span></label>
                    <Select
                      className={isMissing("manager_name") ? "is-invalid" : ""}
                      classNamePrefix="react-select"
                      isLoading={managerLoading}
                      options={managerOptions}
                      value={selectedManager}
                      menuPlacement="top"
                      menuPosition="fixed"
                      onChange={(option) => {
                        setSelectedManager(option || null);
                        setField("manager_name", option?.name || "");
                      }}
                      isClearable
                      placeholder="Select Business Manager"
                    />
                    <input type="hidden" name="manager_name" value={formValues.manager_name} />
                    {isMissing("manager_name") ? (
                      <div className="text-danger small mt-1">Business Manager Name is required.</div>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Date</label>
                    <input
                      className="form-control"
                      disabled
                      value={`${new Date().getDate().toString().padStart(2, "0")}-${(new Date().getMonth() + 1)
                        .toString()
                        .padStart(2, "0")}-${new Date().getFullYear()}`}
                    />
                  </div>
                </div>
              </div>
            </form>

            <div className="modal-footer d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-success" onClick={handleSubmit}>{submitLabel}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
      <div
        id="showLoader"
        className="d-none justify-content-center align-items-center"
        style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.7)", zIndex: 9999 }}
      >
        <div className="spinner-border text-primary"></div>
      </div>
    </>
  );
}

export default CreateInvoiceModal;
